import {
  addDays,
  differenceInDays,
  formatISO,
  parseISO,
  startOfDay,
} from 'date-fns'
import { default as GraphemeSplitter } from 'grapheme-splitter'
import queryString from 'query-string'

import { ENABLE_ARCHIVED_GAMES } from '../constants/settings'
import { NOT_CONTAINED_MESSAGE, WRONG_SPOT_MESSAGE } from '../constants/strings'
import { VALID_GUESSES } from '../constants/validGuesses'
import { WORDS } from '../constants/wordlist'
import { CLASH_STORAGE_KEYS } from './clashConstants'
import { getToday } from './dateutils'
import { getGuessStatuses } from './statuses'

// 1 January 2022 Game Epoch
export const firstGameDate = new Date(2022, 0)
export const periodInDays = 1

// Word Schedule Interface
interface WordSchedule {
  currentWord: string
  currentWordDate: string
  nextWord: string | null
  nextWordScheduledFor: string | null // ISO date string for when to switch
}

// Get the current word from admin settings
const getAdminWord = (): string | null => {
  try {
    const stored = localStorage.getItem(CLASH_STORAGE_KEYS.currentWord)
    if (stored) {
      const parsed = JSON.parse(stored) as WordSchedule

      // Check if we need to switch to next word
      if (parsed.nextWord && parsed.nextWordScheduledFor) {
        const scheduledDate = parseISO(parsed.nextWordScheduledFor)
        const now = new Date()

        if (now >= scheduledDate) {
          // Switch to next word
          const newSchedule: WordSchedule = {
            currentWord: parsed.nextWord,
            currentWordDate: formatISO(now, { representation: 'date' }),
            nextWord: null,
            nextWordScheduledFor: null,
          }
          localStorage.setItem(CLASH_STORAGE_KEYS.currentWord, JSON.stringify(newSchedule))
          return parsed.nextWord
        }
      }

      return parsed.currentWord
    }
  } catch (e) {
    console.error('Error loading admin word:', e)
  }
  return null
}

export const isWordInWordList = (word: string) => {
  return (
    WORDS.includes(localeAwareLowerCase(word)) ||
    VALID_GUESSES.includes(localeAwareLowerCase(word))
  )
}

export const isWinningWord = (word: string, solution: string) => {
  return solution === word
}

// build a set of previously revealed letters - present and correct
// guess must use correct letters in that space and any other revealed letters
// also check if all revealed instances of a letter are used (i.e. two C's)
export const findFirstUnusedReveal = (word: string, guesses: string[], solution: string) => {
  if (guesses.length === 0) {
    return false
  }

  const lettersLeftArray = new Array<string>()
  const guess = guesses[guesses.length - 1]
  const statuses = getGuessStatuses(solution, guess)
  const splitWord = unicodeSplit(word)
  const splitGuess = unicodeSplit(guess)

  for (let i = 0; i < splitGuess.length; i++) {
    if (statuses[i] === 'correct' || statuses[i] === 'present') {
      lettersLeftArray.push(splitGuess[i])
    }
    if (statuses[i] === 'correct' && splitWord[i] !== splitGuess[i]) {
      return WRONG_SPOT_MESSAGE(splitGuess[i], i + 1)
    }
  }

  // check for the first unused letter, taking duplicate letters
  // into account - see issue #198
  let n
  for (const letter of splitWord) {
    n = lettersLeftArray.indexOf(letter)
    if (n !== -1) {
      lettersLeftArray.splice(n, 1)
    }
  }

  if (lettersLeftArray.length > 0) {
    return NOT_CONTAINED_MESSAGE(lettersLeftArray[0])
  }
  return false
}

export const unicodeSplit = (word: string) => {
  return new GraphemeSplitter().splitGraphemes(word)
}

export const unicodeLength = (word: string) => {
  return unicodeSplit(word).length
}

export const localeAwareLowerCase = (text: string) => {
  return process.env.REACT_APP_LOCALE_STRING
    ? text.toLocaleLowerCase(process.env.REACT_APP_LOCALE_STRING)
    : text.toLowerCase()
}

export const localeAwareUpperCase = (text: string) => {
  return process.env.REACT_APP_LOCALE_STRING
    ? text.toLocaleUpperCase(process.env.REACT_APP_LOCALE_STRING)
    : text.toUpperCase()
}

export const getLastGameDate = (today: Date) => {
  const t = startOfDay(today)
  let daysSinceLastGame = differenceInDays(firstGameDate, t) % periodInDays
  return addDays(t, -daysSinceLastGame)
}

export const getNextGameDate = (today: Date) => {
  return addDays(getLastGameDate(today), periodInDays)
}

export const getIndex = (date: Date) => {
  return differenceInDays(startOfDay(date), firstGameDate)
}

export const isValidGameDate = (date: Date) => {
  if (date < firstGameDate || date > getToday()) {
    return false
  }

  return differenceInDays(firstGameDate, date) % periodInDays === 0
}

export const getWordOfDay = (index?: number) => {
  if (typeof index === 'number') {
    if (index < 0 || index >= WORDS.length) {
      throw new Error('Invalid index')
    }
    return localeAwareUpperCase(WORDS[index])
  }

  const adminWord = getAdminWord()
  if (adminWord) {
    return localeAwareUpperCase(adminWord)
  }

  return localeAwareUpperCase(WORDS[0])
}

export const getSolution = (gameDate: Date) => {
  const nextGameDate = getNextGameDate(gameDate)
  const wordOfTheDay = getWordOfDay()
  return {
    solution: wordOfTheDay,
    solutionGameDate: gameDate,
    tomorrow: nextGameDate.valueOf(),
  }
}

export const getGameDate = () => {
  if (getIsLatestGame()) {
    return getToday()
  }

  const parsed = queryString.parse(window.location.search)
  try {
    const d = startOfDay(parseISO(parsed.d!.toString()))
    if (d >= getToday() || d < firstGameDate) {
      setGameDate(getToday())
    }
    return d
  } catch (e) {
    console.log(e)
    return getToday()
  }
}

export const setGameDate = (d: Date) => {
  try {
    if (d < getToday()) {
      window.location.href = '/?d=' + formatISO(d, { representation: 'date' })
      return
    }
  } catch (e) {
    console.log(e)
  }
  window.location.href = '/'
}

export const getIsLatestGame = () => {
  if (!ENABLE_ARCHIVED_GAMES) {
    return true
  }
  const parsed = queryString.parse(window.location.search)
  return parsed === null || !('d' in parsed)
}

// Note: solution, solutionGameDate, and tomorrow are now provided by GameContext
// The synchronous export has been removed to support async Firebase fetching
